/**
 * News Data Models
 * Based on the News DTOs from Spring Boot backend
 */

// News Article DTO
export class NewsArticleDTO {
  constructor(data = {}) {
    // Basic Article Information
    this.id = data.id || '';
    this.title = data.title || '';
    this.description = data.description || '';
    this.content = data.content || '';
    this.url = data.url || '';
    this.publishedAt = data.publishedAt || data.published_at || new Date().toISOString();
    this.source = data.source || '';
    this.author = data.author || '';
    
    // Search Metadata
    this.companyName = data.companyName || data.company_name || '';
    this.ticker = data.ticker || '';
    this.searchDate = data.searchDate || data.search_date || new Date().toISOString();
    this.searchQuery = data.searchQuery || data.search_query || '';
    this.searchType = data.searchType || data.search_type || 'general';
    
    // Analysis Fields
    this.relevanceScore = data.relevanceScore || data.relevance_score || 0;
    this.riskMatches = data.riskMatches || data.risk_matches || [];
    this.riskCategories = data.riskCategories || data.risk_categories || [];
    this.sentiment = data.sentiment || null;
    this.severity = data.severity || null;
    this.topic = data.topic || null;
    this.region = data.region || null;
    this.keywords = data.keywords || [];
    this.impactedCompanies = data.impactedCompanies || data.impacted_companies || [];
    
    // Database Fields
    this.createdAt = data.createdAt || data.created_at || new Date().toISOString();
    this.updatedAt = data.updatedAt || data.updated_at || new Date().toISOString();
  }

  toJSON() {
    return {
      id: this.id,
      title: this.title,
      description: this.description,
      content: this.content,
      url: this.url,
      publishedAt: this.publishedAt,
      source: this.source,
      author: this.author,
      companyName: this.companyName,
      ticker: this.ticker,
      searchDate: this.searchDate,
      searchQuery: this.searchQuery,
      searchType: this.searchType,
      relevanceScore: this.relevanceScore,
      riskMatches: this.riskMatches,
      riskCategories: this.riskCategories,
      sentiment: this.sentiment,
      severity: this.severity,
      topic: this.topic,
      region: this.region,
      keywords: this.keywords,
      impactedCompanies: this.impactedCompanies,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }

  getSentimentColor() {
    // This would be calculated based on sentiment analysis
    // For now, return neutral colors
    return { text: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200' };
  }

  getFormattedDate() {
    return new Date(this.publishedAt).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getRelevanceScoreColor() {
    if (this.relevanceScore >= 0.8) return { text: 'text-green-600', bg: 'bg-green-50' };
    if (this.relevanceScore >= 0.6) return { text: 'text-yellow-600', bg: 'bg-yellow-50' };
    if (this.relevanceScore >= 0.4) return { text: 'text-orange-600', bg: 'bg-orange-50' };
    return { text: 'text-red-600', bg: 'bg-red-50' };
  }

  validate() {
    const errors = [];
    
    if (!this.title || this.title.length > 500) {
      errors.push('Title is required and must not exceed 500 characters');
    }
    
    if (!this.source || this.source.length > 100) {
      errors.push('Source is required and must not exceed 100 characters');
    }
    
    if (this.url && !/^https?:\/\/.+/.test(this.url)) {
      errors.push('URL must be a valid URL format');
    }
    
    if (!['company', 'industry', 'general'].includes(this.searchType)) {
      errors.push('Search type must be company, industry, or general');
    }
    
    if (this.relevanceScore < 0 || this.relevanceScore > 1) {
      errors.push('Relevance score must be between 0.0 and 1.0');
    }
    
    return errors;
  }
}

// News Request DTO
export class NewsRequestDTO {
  constructor(data = {}) {
    this.industryName = data.industryName || data.industry_name || '';
    this.companyName = data.companyName || data.company_name || '';
    this.daysBack = data.daysBack || data.days_back || 30;
    this.includeIndustryRisks = data.includeIndustryRisks !== undefined ? data.includeIndustryRisks : (data.include_industry_risks !== undefined ? data.include_industry_risks : true);
    this.includeCompanyNews = data.includeCompanyNews !== undefined ? data.includeCompanyNews : (data.include_company_news !== undefined ? data.include_company_news : true);
    
    // Optional Enhanced Search
    this.ticker = data.ticker || '';
    this.riskCategories = data.riskCategories || data.risk_categories || [];
    this.keywords = data.keywords || [];
    this.searchQuery = data.searchQuery || data.search_query || '';
    this.maxResults = data.maxResults || data.max_results || 50;
  }

  toJSON() {
    return {
      industryName: this.industryName,
      companyName: this.companyName,
      daysBack: this.daysBack,
      includeIndustryRisks: this.includeIndustryRisks,
      includeCompanyNews: this.includeCompanyNews,
      ticker: this.ticker,
      riskCategories: this.riskCategories,
      keywords: this.keywords,
      searchQuery: this.searchQuery,
      maxResults: this.maxResults
    };
  }

  validate() {
    const errors = [];
    
    if (!this.industryName || this.industryName.length > 255) {
      errors.push('Industry name is required and must not exceed 255 characters');
    }
    
    if (!this.companyName || this.companyName.length > 255) {
      errors.push('Company name is required and must not exceed 255 characters');
    }
    
    if (this.daysBack < 1 || this.daysBack > 365) {
      errors.push('Days back must be between 1 and 365');
    }
    
    if (this.maxResults < 1 || this.maxResults > 1000) {
      errors.push('Max results must be between 1 and 1000');
    }
    
    return errors;
  }
}

// News Response DTO
export class NewsResponseDTO {
  constructor(data = {}) {
    // Request Information
    this.requestId = data.requestId || data.request_id || '';
    this.companyName = data.companyName || data.company_name || '';
    this.ticker = data.ticker || '';
    this.industryName = data.industryName || data.industry_name || '';
    this.daysBack = data.daysBack || data.days_back || 30;
    this.includeIndustryRisks = data.includeIndustryRisks || data.include_industry_risks || true;
    this.includeCompanyNews = data.includeCompanyNews || data.include_company_news || true;
    
    // News Articles Found
    this.industryRiskNews = (data.industryRiskNews || data.industry_risk_news || []).map(article => new NewsArticleDTO(article));
    this.companyNews = (data.companyNews || data.company_news || []).map(article => new NewsArticleDTO(article));
    this.allNews = (data.allNews || data.all_news || []).map(article => new NewsArticleDTO(article));
    
    // Analysis Results
    this.totalArticlesFound = data.totalArticlesFound || data.total_articles_found || 0;
    this.industryRiskArticlesCount = data.industryRiskArticlesCount || data.industry_risk_articles_count || 0;
    this.companyNewsArticlesCount = data.companyNewsArticlesCount || data.company_news_articles_count || 0;
    this.averageRelevanceScore = data.averageRelevanceScore || data.average_relevance_score || 0;
    this.riskCategoriesFound = data.riskCategoriesFound || data.risk_categories_found || [];
    this.riskMatchesFound = data.riskMatchesFound || data.risk_matches_found || [];
    
    // Processing Metadata
    this.processingStartTime = data.processingStartTime || data.processing_start_time || new Date().toISOString();
    this.processingEndTime = data.processingEndTime || data.processing_end_time || new Date().toISOString();
    this.processingDurationMs = data.processingDurationMs || data.processing_duration_ms || 0;
    this.savedToDatabase = data.savedToDatabase || data.saved_to_database || false;
    this.savedNewsIds = data.savedNewsIds || data.saved_news_ids || [];
    
    // Error Handling
    this.warnings = data.warnings || [];
    this.errors = data.errors || [];
    this.success = data.success || false;
    this.message = data.message || '';
  }

  toJSON() {
    return {
      requestId: this.requestId,
      companyName: this.companyName,
      ticker: this.ticker,
      industryName: this.industryName,
      daysBack: this.daysBack,
      includeIndustryRisks: this.includeIndustryRisks,
      includeCompanyNews: this.includeCompanyNews,
      industryRiskNews: this.industryRiskNews.map(article => article.toJSON()),
      companyNews: this.companyNews.map(article => article.toJSON()),
      allNews: this.allNews.map(article => article.toJSON()),
      totalArticlesFound: this.totalArticlesFound,
      industryRiskArticlesCount: this.industryRiskArticlesCount,
      companyNewsArticlesCount: this.companyNewsArticlesCount,
      averageRelevanceScore: this.averageRelevanceScore,
      riskCategoriesFound: this.riskCategoriesFound,
      riskMatchesFound: this.riskMatchesFound,
      processingStartTime: this.processingStartTime,
      processingEndTime: this.processingEndTime,
      processingDurationMs: this.processingDurationMs,
      savedToDatabase: this.savedToDatabase,
      savedNewsIds: this.savedNewsIds,
      warnings: this.warnings,
      errors: this.errors,
      success: this.success,
      message: this.message
    };
  }

  getProcessingDurationFormatted() {
    if (this.processingDurationMs < 1000) {
      return `${this.processingDurationMs}ms`;
    }
    return `${(this.processingDurationMs / 1000).toFixed(2)}s`;
  }

  getTotalArticlesCount() {
    return this.industryRiskArticlesCount + this.companyNewsArticlesCount;
  }
}

// News Statistics DTO
export class NewsStatisticsDTO {
  constructor(data = {}) {
    this.totalArticles = data.totalArticles || data.total_articles || 0;
    this.newsCountBySource = data.newsCountBySource || data.news_count_by_source || {};
    this.newsCountByTicker = data.newsCountByTicker || data.news_count_by_ticker || {};
  }

  toJSON() {
    return {
      totalArticles: this.totalArticles,
      newsCountBySource: this.newsCountBySource,
      newsCountByTicker: this.newsCountByTicker
    };
  }
}

// Search Type Enum
export const SEARCH_TYPES = {
  COMPANY: 'company',
  INDUSTRY: 'industry',
  GENERAL: 'general'
};

// Model Factory Functions
export const createNewsArticle = (data) => new NewsArticleDTO(data);
export const createNewsRequest = (data) => new NewsRequestDTO(data);
export const createNewsResponse = (data) => new NewsResponseDTO(data);
export const createNewsStatistics = (data) => new NewsStatisticsDTO(data);

// Utility Functions
export const formatNewsDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const getRelevanceScoreColor = (score) => {
  if (score >= 0.8) return { text: 'text-green-600', bg: 'bg-green-50' };
  if (score >= 0.6) return { text: 'text-yellow-600', bg: 'bg-yellow-50' };
  if (score >= 0.4) return { text: 'text-orange-600', bg: 'bg-orange-50' };
  return { text: 'text-red-600', bg: 'bg-red-50' };
};

export const validateNewsUrl = (url) => {
  if (!url) return true; // Optional field
  return /^https?:\/\/.+/.test(url);
};
